import {
  skyFlightFrame,
  skyFlightPointToWorld,
  skyFlightVectorToWorld,
  skyFlightPointToLocal,
  skyFlightVectorToLocal,
} from './92-sky-flight-frame.mjs';
import { skyLeviathanArticulation, SKY_LEVIATHAN_ASSEMBLY } from './95-sky-leviathan-space.mjs';
import { skyLeviathanMeshSweep } from './96-sky-leviathan-contacts.mjs';
import { compileSkyRigidAssembly } from './103-sky-rigid-assembly.mjs';
import { SKY_WORLD } from './86-sky-world-scale.mjs';

function pinnedFrame(part) {
  const m = part.pivotToHull;
  return skyFlightFrame({
    origin: { x: m[12], y: m[13], z: m[14] },
    right: { x: m[0], y: m[1], z: m[2] },
    up: { x: m[4], y: m[5], z: m[6] },
    back: { x: m[8], y: m[9], z: m[10] },
  });
}
function pinnedRestFrame(part) {
  const global = pinnedFrame(part);
  if (!part.parent) return global;
  const parent = pinnedFrame(SKY_LEVIATHAN_ASSEMBLY.armorParts.find((p) => p.id === part.parent));
  return {
    origin: skyFlightPointToLocal(parent, global.origin),
    right: skyFlightVectorToLocal(parent, global.right),
    up: skyFlightVectorToLocal(parent, global.up),
    back: skyFlightVectorToLocal(parent, global.back),
  };
}
export const SKY_LEVIATHAN_ARMOR_PARTS = Object.freeze(
  SKY_LEVIATHAN_ASSEMBLY.armorParts.map((part) =>
    Object.freeze({
      id: part.id,
      parentId: part.parent,
      mesh: part.mesh,
      node: part.node,
      restFrame: Object.freeze(
        Object.fromEntries(
          Object.entries(pinnedRestFrame(part)).map(([key, value]) => [key, Object.freeze(value)]),
        ),
      ),
    }),
  ),
);
const accepted = new WeakSet();
const axes = ['x', 'y', 'z'];
const finite = (p) =>
  p && axes.every((axis) => Number.isFinite(p[axis]) && Math.abs(p[axis]) <= 150);
/** Compile the pinned seven-part Leviathan asset using the shared rigid
 * geometry boundary. Only this wrapper authorizes handles for this encounter;
 * a valid generic assembly cannot replace its measured armor contract. */
export function createSkyLeviathanCollision(parts) {
  const handle = compileSkyRigidAssembly(parts, SKY_LEVIATHAN_ARMOR_PARTS);
  if (handle) accepted.add(handle);
  return handle;
}

const identity = () => ({
  origin: { x: 0, y: 0, z: 0 },
  right: { x: 1, y: 0, z: 0 },
  up: { x: 0, y: 1, z: 0 },
  back: { x: 0, y: 0, z: 1 },
});
const compose = (parent, local) => ({
  origin: skyFlightPointToWorld(parent, local.origin),
  right: skyFlightVectorToWorld(parent, local.right),
  up: skyFlightVectorToWorld(parent, local.up),
  back: skyFlightVectorToWorld(parent, local.back),
});
function rotation({ x = 0, y = 0, z = 0 } = {}) {
  const frame = identity();
  for (const key of ['right', 'up', 'back']) {
    const p = frame[key],
      az = p.x * Math.cos(z) - p.y * Math.sin(z),
      bz = p.x * Math.sin(z) + p.y * Math.cos(z);
    const ax = az * Math.cos(y) + p.z * Math.sin(y),
      cz = -az * Math.sin(y) + p.z * Math.cos(y);
    frame[key] = {
      x: ax,
      y: bz * Math.cos(x) - cz * Math.sin(x),
      z: bz * Math.sin(x) + cz * Math.cos(x),
    };
  }
  return frame;
}

/** Actual part frames from the same local joint rotations used by the renderer.
 * Parent tails carry child pivots. Invalid/uncompiled handles return null. */
export function skyLeviathanArmorFrames(handle, hullFrame, articulation) {
  const hull = skyFlightFrame(hullFrame);
  if (!accepted.has(handle) || !hull) return null;
  if (
    articulation &&
    handle.parts.some(
      (part) =>
        articulation[part.id] &&
        !axes.every(
          (axis) =>
            Number.isFinite(articulation[part.id][axis] ?? 0) &&
            Math.abs(articulation[part.id][axis] ?? 0) <= Math.PI,
        ),
    )
  )
    return null;
  const frames = {};
  function resolve(part) {
    if (frames[part.id]) return frames[part.id];
    const parent = part.parentId ? resolve(handle.parts.find((p) => p.id === part.parentId)) : hull;
    frames[part.id] = compose(compose(parent, part.restFrame), rotation(articulation?.[part.id]));
    return frames[part.id];
  }
  for (const part of handle.parts) resolve(part);
  return frames;
}

/** Contact proxies reference exact immutable facets and current rigid frames;
 * their enclosing radius only accelerates acquisition, never accepts a hit. */
export function skyLeviathanArmorTargets(state) {
  const handle = state.leviathanCollision,
    live = state.leviathan;
  if (!accepted.has(handle) || !live) return [];
  live.articulation = skyLeviathanArticulation(live.encounter, live.systems, live.reducedMotion);
  const frames = skyLeviathanArmorFrames(handle, live.hullFrame, live.articulation);
  if (!frames) return [];
  live.armorIds ??= {};
  return handle.parts.map((part) => ({
    id: (live.armorIds[part.id] ??= state.nextId++),
    kind: 'pillar',
    hp: Infinity,
    value: 0,
    ...skyFlightPointToWorld(frames[part.id], part.center),
    radius: part.radius,
    contactShape: 'mesh',
    triangles: part.triangles,
    contactFrame: frames[part.id],
    vulnerable: false,
    leviathanWall: `armor-${part.id}`,
    leviathanArmor: part.id,
  }));
}

/** Actual pilot contact against moving tail facets. Both endpoints are sampled
 * in their corresponding rigid part frames, so a sweeping tail cannot damage
 * a remote ellipsoid while its visible surface stays elsewhere. */
export function skyLeviathanTailContact(state, previousPilot) {
  const live = state.leviathan;
  if (!live?.armor?.length || !finite(previousPilot)) return null;
  const pilot = { x: state.x, y: state.y, z: state.pilotZ };
  for (const part of live.armor.filter((p) => p.leviathanArmor.startsWith('tail_'))) {
    const previous = live.previousArmor?.find((p) => p.id === part.id) ?? part;
    const hit = skyLeviathanMeshSweep(
      skyFlightPointToLocal(previous.contactFrame, previousPilot),
      skyFlightPointToLocal(part.contactFrame, pilot),
      part.triangles,
      SKY_WORLD.pilotContactRadius,
    );
    if (hit) return { ...hit, partId: part.leviathanArmor };
  }
  return null;
}

/** Bind only a compiled asset handle. Readiness may refresh a frozen run after
 * context restoration, but cannot replace collidable artwork during live play. */
export function installSkyLeviathanCollision(state, handle) {
  if (
    !state ||
    !accepted.has(handle) ||
    (state.status === 'playing' &&
      (state.leviathanCollision || state.leviathan?.encounter.age > 0) &&
      state.leviathanCollision !== handle)
  )
    return false;
  state.leviathanCollision = handle;
  if (state.leviathan) {
    state.leviathan.armor = skyLeviathanArmorTargets(state);
    state.enemies = [
      ...state.enemies.filter((target) => !target.leviathanArmor),
      ...state.leviathan.armor,
    ];
  }
  return true;
}
